import { Truck, ShieldCheck, Wrench } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

/**
 * WhyChooseTheraTec — cinematic dark trust slab.
 * Big italic numerals, editorial spacing, gold-accent hover reveal.
 */

const REASONS = [
  {
    n: "01",
    icon: Truck,
    title: "White-Glove Delivery",
    body: "Concierge crating, freight coordination, and on-site placement inside your suite — we uncrate, position, and haul the packaging away.",
  },
  {
    n: "02",
    icon: ShieldCheck,
    title: "ASME-Certified Build",
    body: "Every chamber constructed to ASME PVHO-1 pressure standards and pressure-tested to 1.5× spec before it leaves the facility.",
  },
  {
    n: "03",
    icon: Wrench,
    title: "ISO Technicians On Demand",
    body: "Direct line to certified specialists for install, service, and staff training — never a call-center escalation queue.",
  },
];

export default function WhyChooseTheraTec() {
  const [headerRef, headerRevealed] = useReveal();
  const [gridRef, gridRevealed] = useReveal();
  return (
    <section
      data-testid="why-choose-theratec"
      className="relative overflow-hidden bg-[#1C1C1C] text-[#FAF9F6] py-10 lg:py-14"
    >
      {/* Champagne halo top-right */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(48vw 32vw at 85% -10%, rgba(201,169,97,0.18), transparent 65%), radial-gradient(38vw 28vw at 10% 110%, rgba(201,169,97,0.10), transparent 65%)",
        }}
      />

      {/* Kinetic word — very faint editorial backdrop */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex whitespace-nowrap select-none pointer-events-none"
        style={{ animation: "tt-type-drift 90s linear infinite" }}
      >
        <span
          className="font-serif-editorial italic"
          style={{
            fontSize: "clamp(110px, 15vw, 200px)",
            color: "rgba(250,249,246,0.035)",
            letterSpacing: "-0.03em",
            paddingRight: "6vw",
            lineHeight: 1,
          }}
        >
          THE&nbsp;STANDARD
        </span>
        <span
          className="font-serif-editorial italic"
          style={{
            fontSize: "clamp(110px, 15vw, 200px)",
            color: "rgba(250,249,246,0.035)",
            letterSpacing: "-0.03em",
            paddingRight: "6vw",
            lineHeight: 1,
          }}
        >
          THE&nbsp;STANDARD
        </span>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={headerRef} className={`mb-8 lg:mb-10 max-w-3xl tt-reveal ${headerRevealed ? "is-revealed" : ""}`}>
          <div className="overline text-[#C9A961] inline-flex items-center gap-2">
            <span className="h-px w-6 bg-[#C9A961]" />
            The TheraTec Standard
          </div>
          <h2 className="font-serif-editorial text-2xl md:text-3xl lg:text-4xl mt-3 leading-[1.05] tracking-tight">
            Why clinics <em className="italic tt-shimmer-gold">choose TheraTec.</em>
          </h2>
        </div>

        {/* Reasons — editorial split, hairline dividers */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#FAF9F6]/12">
          {REASONS.map((r, i) => {
            const Icon = r.icon;
            return (
              <article
                key={r.title}
                data-testid={`why-reason-${i}`}
                className={`group relative px-0 md:px-6 lg:px-8 py-6 first:pt-0 md:first:pt-6 first:pl-0 last:pr-0 tt-reveal ${gridRevealed ? "is-revealed" : ""}`}
                style={{ transitionDelay: gridRevealed ? `${i * 140}ms` : "0ms" }}
              >
                {/* Numeric badge */}
                <div className="flex items-baseline gap-3 mb-3">
                  <span
                    className="font-serif-editorial italic leading-none"
                    style={{
                      fontSize: "clamp(36px, 3.4vw, 48px)",
                      background: "linear-gradient(180deg,#E8D6A8 0%, #C9A961 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {r.n}
                  </span>
                  <span
                    className="h-7 w-7 flex items-center justify-center border border-[#C9A961]/40 group-hover:border-[#C9A961] transition-colors duration-500"
                    style={{ background: "rgba(201,169,97,0.06)" }}
                  >
                    <Icon size={13} strokeWidth={1.5} className="text-[#C9A961]" />
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif-editorial text-lg md:text-xl lg:text-[22px] leading-tight mb-3">
                  {r.title}
                </h3>

                {/* Gold underline reveal on hover */}
                <div className="relative mb-3 h-px w-full overflow-hidden">
                  <span className="absolute inset-y-0 left-0 w-6 bg-[#C9A961]" />
                  <span
                    aria-hidden
                    className="absolute inset-y-0 left-6 bg-[#C9A961]/60 w-0 group-hover:w-full transition-all duration-[700ms] ease-out"
                  />
                </div>

                {/* Body */}
                <p className="text-[13px] text-[#FAF9F6]/72 leading-relaxed max-w-sm">
                  {r.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
